import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import i18next from "i18next";
import { meQueryOptions } from "../auth";

/**
 * Applies the signed-in user's `preferred_language` to the UI.
 *
 * The identity profile carries the language the user chose on their account. Without this, a
 * user whose account says French would still get the browser/localStorage-detected language —
 * the stored preference would silently only affect server-rendered notifications, never the app
 * they are looking at.
 *
 * The backend enum still allows `ar`, but the frontend ships French and English only.
 * `ProfileSchema` reads any other value as French, so an account stored as `ar` opens in French
 * instead of asking i18next for a bundle that does not exist.
 *
 * `changeLanguage` cascades: i18next's detector caches the value to localStorage, and the
 * `languageChanged` listener wired in main.tsx updates `<html lang/dir>`. So one call here
 * re-orients the whole app.
 *
 * `enabled: false` is deliberate and load-bearing: this component REACTS to identity, it must
 * never fetch it. It subscribes to the /me cache entry (populated by the route guards and seeded
 * by login) and re-renders when it changes — mounted at the app root, an enabled query would fire
 * a doomed /me on the public login screen on every visit.
 */
export function PreferredLanguageSync() {
  const { data: profile } = useQuery({ ...meQueryOptions, enabled: false });
  const preferred = profile?.user.preferred_language;

  useEffect(() => {
    if (!preferred) return;
    // Guard the no-op: changeLanguage always emits `languageChanged`, which would re-write
    // <html> attributes and re-render every subscriber on each profile refetch.
    if (i18next.resolvedLanguage === preferred) return;
    void i18next.changeLanguage(preferred);
  }, [preferred]);

  return null;
}
